import { ArrowLeftIcon, AutomateIcon, AutomateSettingsIcon } from '@eleveo/icons-react';
import { Button, Col, Icon as SharedIcon, Row, Tooltip } from '@zoom/components';
import { TFunction } from 'i18next';
import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import { Link } from 'react-router-dom';
import { Form } from 'reactstrap';

import { DaySwitcherBar } from './DaySwitcherBar';
import { ExportSchedule } from './ExportSchedule';
import { UndoRedo } from './UndoRedo';
import { Permissions, useAuth } from '../../../app/AuthorizationProvider';
import { DatePickerValueType } from '../../../components/DatePicker/DatePicker';
import Icon from '../../../components/Icon';
import { Separator } from '../../../components/layout/Separator';
import { useSidebar } from '../../../components/layout/Sidebar';
import { LockType } from '../../../graphql/components';
import { PATH_SCHEDULING_STUDIO } from '../../../router/routes';
import { CreateEditView, CreateEditViewType } from '../../../types';
import {
  ADD_PERSONS_BUTTON_TAG,
  CANCEL_BUTTON,
  CREATE_SCHEDULE_FORM_TAG,
  EDIT_SCHEDULE_FORM_TAG,
  PREVIOUS_STEP_BUTTON,
  SAVE_BUTTON,
  TOGGLE_CONFIGURATION_BUTTON_TAG,
  getButtonTestId,
} from '../constants';
import { ScheduleHistory } from '../types';
import { getPathname } from '../utils/pathname';

export interface SchedulingHeaderProps {
  action: CreateEditViewType;
  activeSchedulesIds: string[];
  handleCreateAutoScheduleJob?: () => void;
  endTime: number;
  existAgentWithAssignedShiftTemplate: boolean;
  handleDateChange: (value: DatePickerValueType) => void;
  handleNextDay: () => void;
  handlePreviousDay: () => void;
  handlePreviousStep: () => void;
  handleRewindSchedule: (statePosition: number) => void;
  handleSubmit: () => void;
  handleToggleAddPersonsSidebar: () => void;
  handleToggleScheduleConfiguration: () => void;
  isAutoScheduleLimitExceeded: boolean;
  isAutoScheduleRangeOverlapping: boolean;
  isEdit: boolean;
  isProduction: boolean;
  isScheduleConfigurationSidebarOpen: boolean;
  isView: boolean;
  lockType: LockType;
  pickedTimeLocal: number;
  pickedTimeViewed: number;
  scheduleEndsInFuture: boolean;
  scheduleHistory: ScheduleHistory;
  scheduleId?: string;
  startTime: number;
  t: TFunction;
  timezone: string;
  updateProductionState: () => void;
}

export const SchedulingHeader = ({
  action,
  activeSchedulesIds,
  handleCreateAutoScheduleJob,
  endTime,
  existAgentWithAssignedShiftTemplate,
  handleDateChange,
  handleNextDay,
  handlePreviousDay,
  handlePreviousStep,
  handleRewindSchedule,
  handleSubmit,
  handleToggleAddPersonsSidebar,
  handleToggleScheduleConfiguration,
  isAutoScheduleLimitExceeded,
  isAutoScheduleRangeOverlapping,
  isEdit,
  isProduction,
  isScheduleConfigurationSidebarOpen,
  isView,
  lockType,
  pickedTimeLocal,
  pickedTimeViewed,
  scheduleEndsInFuture,
  scheduleHistory,
  scheduleId,
  startTime,
  t,
  timezone,
  updateProductionState,
}: SchedulingHeaderProps) => {
  const { isSidebarOpen } = useSidebar();

  const { userHasPermissions } = useAuth();
  const hasExportSchedulePermission = userHasPermissions([Permissions.ExportSchedule]);
  const hasCreateAutoSchedulePermission = userHasPermissions([Permissions.CreateAutoScheduleJob]);

  const lockTypeWrite = lockType === LockType.Write;

  const AutoScheduleButton = (
    <Button
      className="button--icon--left-hand-side mr-2 py-0 h-100 my-auto"
      color="primary"
      // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
      disabled={!handleCreateAutoScheduleJob || lockTypeWrite || isView || isAutoScheduleRangeOverlapping}
      onClick={handleCreateAutoScheduleJob}
      data-test-id={getButtonTestId(action, 2, lockTypeWrite ? 'in-progress-button' : 'auto-schedule-button')}
    >
      <AutomateIcon className="text-xl" />
      {lockTypeWrite ? t(`layout.inProgress`) : t(`schedule.autoSchedule.title`)}
    </Button>
  );

  const getAutoScheduleTooltipMessage = () => {
    let tooltipMessageKey = 'schedule.autoSchedule.title';

    if (!isView) {
      if (activeSchedulesIds.length) {
        tooltipMessageKey = 'schedule.autoSchedule.createDisabledLimitReached';
      } else if (!scheduleEndsInFuture) {
        tooltipMessageKey = 'schedule.autoSchedule.createDisabledScheduleInPast';
      } else if (isAutoScheduleLimitExceeded) {
        tooltipMessageKey = 'schedule.autoSchedule.createDisabledLimitForUserOrRangeExceeded';
      } else if (!existAgentWithAssignedShiftTemplate) {
        tooltipMessageKey = 'schedule.autoSchedule.createDisabledNoShiftTemplate';
      } else {
        tooltipMessageKey = 'schedule.autoSchedule.createDisabled';
      }
    } else if (lockTypeWrite) {
      tooltipMessageKey = 'schedule.autoSchedule.inProgress';
    } else {
      tooltipMessageKey = 'schedule.autoSchedule.title';
    }

    return t(tooltipMessageKey);
  };

  return (
    <FinalForm
      initialValues={{ date: pickedTimeLocal || startTime }}
      onSubmit={handleSubmit}
      render={({ handleSubmit: formHandleSubmit }) => (
        <Form onSubmit={formHandleSubmit} data-test-id={isEdit ? EDIT_SCHEDULE_FORM_TAG : CREATE_SCHEDULE_FORM_TAG}>
          <Row>
            <Col xs="auto">
              <Button
                color="primary"
                data-test-id={getButtonTestId(action, 2, PREVIOUS_STEP_BUTTON)}
                kind="icon"
                tag={Link}
                title={t('layout.steps.previous')}
                to={getPathname(action, scheduleId)}
                onClick={(e: any) => {
                  e.preventDefault();
                  handlePreviousStep();
                }}
              >
                <ArrowLeftIcon className="text-white" />
              </Button>
            </Col>
            <Separator />
            {!isView && (
              <Col xs="auto">
                <Button color="secondary" data-test-id={getButtonTestId(action, 2, SAVE_BUTTON)} type="submit">
                  {t('layout.action.save')}
                </Button>
              </Col>
            )}
            <Col xs="auto">
              <Button
                className="button--icon--left-hand-side scheduling-header-form__cancel-button"
                color="info"
                data-test-id={getButtonTestId(action, 2, CANCEL_BUTTON)}
                tag={Link}
                to={PATH_SCHEDULING_STUDIO}
              >
                <Icon color="#eb651c" kind="cross" size={10} />
                {t('layout.action.cancel')}
              </Button>
            </Col>
            <Separator />
            <Col xs="auto">
              <DaySwitcherBar
                startTime={new Date(startTime)}
                endTime={new Date(endTime)}
                pickedTime={new Date(pickedTimeLocal)}
                handlePreviousDay={handlePreviousDay}
                handleNextDay={handleNextDay}
                handleDateChange={handleDateChange}
                t={t}
              />
            </Col>
            {!isView && (
              <>
                <Separator />
                <Col xs="auto">
                  <Button
                    active={isSidebarOpen && !isScheduleConfigurationSidebarOpen}
                    className="button--icon--left-hand-side"
                    color="info"
                    data-test-id={ADD_PERSONS_BUTTON_TAG}
                    onClick={handleToggleAddPersonsSidebar}
                  >
                    <Icon color="#239ed8" kind="plus" size={20} />
                    {t('person.addPeople')}
                  </Button>
                </Col>
              </>
            )}
            <Separator />
            <Col xs="auto">
              <Button
                // utility classes preventing broken position of icon and title when using icon size above 20
                className="button--icon--left-hand-side py-0 h-100 my-auto"
                color={isProduction ? 'info' : 'secondary'}
                // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
                disabled={isView}
                onClick={updateProductionState}
                data-test-id={getButtonTestId(
                  action,
                  2,
                  isProduction ? 'put-to-draft-button' : 'put-to-production-button'
                )}
              >
                <SharedIcon
                  color={isProduction ? 'orange' : 'white'}
                  kind={isProduction ? 'remove_from_production' : 'put_to_production'}
                  size={24}
                />
                {t(`layout.action.${isProduction ? 'putToDraft' : 'putToProduction'}`)}
              </Button>
            </Col>
            <Separator />
            {hasCreateAutoSchedulePermission && (
              <>
                <Col xs="auto">
                  {handleCreateAutoScheduleJob ? (
                    AutoScheduleButton
                  ) : (
                    <Tooltip placement="bottom" message={getAutoScheduleTooltipMessage()}>
                      {AutoScheduleButton}
                    </Tooltip>
                  )}
                  {!isView && (
                    <Button
                      active={isScheduleConfigurationSidebarOpen}
                      className="scheduling-header-form__configuration-button"
                      data-test-id={TOGGLE_CONFIGURATION_BUTTON_TAG}
                      kind="info"
                      onClick={handleToggleScheduleConfiguration}
                      title={t('schedule.autoSchedule.scheduleConfiguration')}
                      // eslint-disable-next-line no-restricted-syntax -- invalid rule for components from @zoom/components
                      disabled={!scheduleEndsInFuture}
                    >
                      <AutomateSettingsIcon className="text-2xl text-chart-300" />
                    </Button>
                  )}
                </Col>
                {!isView && <Separator />}
              </>
            )}
            <div className="scheduling-header-form__right-side-wrapper">
              {action !== CreateEditView.VIEW && (
                <UndoRedo scheduleHistory={scheduleHistory} handleRewindSchedule={handleRewindSchedule} t={t} />
              )}
              {(isEdit || isView) && scheduleId && hasExportSchedulePermission && (
                <>
                  <Separator className="my-0" />
                  <ExportSchedule currentDate={pickedTimeViewed} scheduleId={scheduleId} timezone={timezone} />
                </>
              )}
            </div>
          </Row>
        </Form>
      )}
    />
  );
};
